/**
 * <klapi-slider> : slider générique à base de scroll-snap.
 *
 * Structure attendue (tout est optionnel sauf la piste) :
 *   <klapi-slider>
 *     <div data-piste>…éléments…</div>      piste qui défile (overflow-x: auto + scroll-snap)
 *     <button data-prec>…</button>          page précédente
 *     <button data-suiv>…</button>          page suivante
 *     <div data-points></div>               points de pagination, générés
 *     <span data-compteur></span>           compteur "01 / 04", mis à jour
 *   </klapi-slider>
 *
 * Attributs :
 *   data-par="element"  un pas = un élément (par défaut : une page = largeur visible)
 *
 * Clavier : flèches gauche / droite quand le focus est dans la piste.
 * Souris : la piste se glisse à la main (le doigt et le trackpad défilent nativement).
 * Les boutons sont désactivés aux extrémités ; le défilement respecte prefers-reduced-motion
 * via scroll-behavior (géré en CSS).
 */
// Le script peut être inclus par plusieurs sections : l'élément n'est déclaré qu'une fois
if (!customElements.get('klapi-slider')) {
  class KlapiSlider extends HTMLElement {
    connectedCallback() {
      this.piste = this.querySelector('[data-piste]');
      if (!this.piste) return;
      this.prec = this.querySelector('[data-prec]');
      this.suiv = this.querySelector('[data-suiv]');
      this.points = this.querySelector('[data-points]');
      this.compteur = this.querySelector('[data-compteur]');
      this.parElement = this.dataset.par === 'element';

      this.prec?.addEventListener('click', () => this.aller(this.index() - 1));
      this.suiv?.addEventListener('click', () => this.aller(this.index() + 1));
      this.piste.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.aller(this.index() + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.aller(this.index() - 1);
        }
      });

      let attente;
      this.piste.addEventListener('scroll', () => {
        cancelAnimationFrame(attente);
        attente = requestAnimationFrame(() => this.maj());
      });

      this.activerGlisser();

      this.observateur = new ResizeObserver(() => this.construire());
      this.observateur.observe(this.piste);
    }

    disconnectedCallback() {
      this.observateur?.disconnect();
    }

    /** Glisser à la souris : suit le pointeur, puis se cale sur la page suivante / précédente. */
    activerGlisser() {
      let depart = null;
      let glisse = false;

      this.piste.addEventListener('dragstart', (event) => event.preventDefault());

      this.piste.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || this.hasAttribute('data-inactif')) return;
        depart = { x: event.clientX, gauche: this.piste.scrollLeft, index: this.index() };
        glisse = false;
      });

      this.piste.addEventListener('pointermove', (event) => {
        if (!depart) return;
        const ecart = event.clientX - depart.x;
        if (!glisse && Math.abs(ecart) < 6) return;
        if (!glisse) {
          glisse = true;
          this.piste.setPointerCapture(event.pointerId);
          this.piste.setAttribute('data-glisse', '');
        }
        this.piste.scrollLeft = depart.gauche - ecart;
      });

      const relacher = (event) => {
        if (!depart) return;
        const ecart = event.clientX - depart.x;
        const index = depart.index;
        depart = null;
        if (!glisse) return;
        this.piste.removeAttribute('data-glisse');
        if (ecart < -40) this.aller(index + 1);
        else if (ecart > 40) this.aller(index - 1);
        else this.aller(index);
      };
      this.piste.addEventListener('pointerup', relacher);
      this.piste.addEventListener('pointercancel', relacher);

      // Après un glisser, le clic qui suit ne doit pas ouvrir un lien
      this.piste.addEventListener(
        'click',
        (event) => {
          if (!glisse) return;
          event.preventDefault();
          event.stopPropagation();
          glisse = false;
        },
        true
      );
    }

    elements() {
      return [...this.piste.children].filter((el) => el.offsetParent !== null);
    }

    /** Nombre de positions : éléments, ou pages de la largeur visible. */
    total() {
      if (this.parElement) return this.elements().length;
      const { scrollWidth, clientWidth } = this.piste;
      return Math.max(1, Math.ceil((scrollWidth - 4) / clientWidth));
    }

    index() {
      const { scrollLeft, scrollWidth, clientWidth } = this.piste;
      if (scrollLeft + clientWidth >= scrollWidth - 4) return this.total() - 1;
      if (this.parElement) {
        const elements = this.elements();
        const depart = elements[0]?.offsetLeft ?? 0;
        let i = 0;
        elements.forEach((el, j) => {
          if (el.offsetLeft - depart <= scrollLeft + 4) i = j;
        });
        return i;
      }
      return Math.round(scrollLeft / clientWidth);
    }

    aller(i) {
      const borne = Math.max(0, Math.min(i, this.total() - 1));
      let gauche;
      if (this.parElement) {
        const elements = this.elements();
        gauche = (elements[borne]?.offsetLeft ?? 0) - (elements[0]?.offsetLeft ?? 0);
      } else {
        gauche = borne * this.piste.clientWidth;
      }
      this.piste.scrollTo({ left: gauche });
    }

    construire() {
      const total = this.total();
      this.toggleAttribute('data-inactif', total < 2);
      if (this.points) {
        this.points.replaceChildren(
          ...Array.from({ length: total }, (_, i) => {
            const bouton = document.createElement('button');
            bouton.type = 'button';
            bouton.setAttribute('aria-label', `Aller à ${i + 1} sur ${total}`);
            bouton.addEventListener('click', () => this.aller(i));
            return bouton;
          })
        );
        this.points.hidden = total < 2;
      }
      this.maj();
    }

    maj() {
      const total = this.total();
      const i = this.index();
      if (this.prec) this.prec.disabled = i <= 0;
      if (this.suiv) this.suiv.disabled = i >= total - 1;
      this.points?.querySelectorAll('button').forEach((bouton, j) => {
        if (j === i) bouton.setAttribute('aria-current', 'true');
        else bouton.removeAttribute('aria-current');
      });
      this.style.setProperty('--k-slider-index', i);
      this.style.setProperty('--k-slider-total', total);
      if (this.compteur) {
        const pad = (n) => String(n).padStart(2, '0');
        this.compteur.textContent = `${pad(i + 1)} / ${pad(total)}`;
      }
    }
  }

  customElements.define('klapi-slider', KlapiSlider);
}
